import React, { useState } from "react";
import { useMyGames, useShortlistGames } from "@seabrookstudios/pitch2table";
import { FilterableGamesList } from "../games/FilterableGamesList";
import { FullScreenDialog } from "../core/FullScreenDialog";
import Add from "@mui/icons-material/Add";
import { useQueryClient } from "@tanstack/react-query";
import { PublisherSubmissionsByPublisherId } from "../games/query-keys";
import { useSnackbar } from "notistack";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").PublisherId} props.publisherId
 * @param {import("@seabrookstudios/types").GameId[]} props.existingGameIds - Game IDs that are already submitted
 * @param {import("react").ReactNode} [props.CustomControl] - Custom control to trigger the dialog (e.g., IconButton)
 */
export const AddGamesToPublisherDialog = ({ publisherId, existingGameIds = [], CustomControl }) => {
  const { data: allGames = [], isLoading } = useMyGames();
  const [selectedGames, setSelectedGames] = useState(/** @type {import("@seabrookstudios/types").GameId[]} */ ([]));

  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const shortlist = useShortlistGames(publisherId);

  const availableGames = allGames.filter((game) => !existingGameIds.includes(game.id));

  /**
   * @param {import("@seabrookstudios/types").GameId} gameId
   */
  const handleToggleGame = (gameId) => {
    setSelectedGames((current) => {
      if (current.includes(gameId)) {
        return current.filter((id) => id !== gameId);
      }

      return [...current, gameId];
    });
  };

  const handleSave = async () => {
    if (selectedGames.length === 0) {
      return;
    }

    try {
      await shortlist.mutateAsync({ gameIds: selectedGames });

      await queryClient.invalidateQueries({ queryKey: PublisherSubmissionsByPublisherId(publisherId) });

      setSelectedGames([]);
    } catch (_error) {
      enqueueSnackbar("Failed to add games to submissions", { variant: "error" });
    }
  };

  return (
    <FullScreenDialog
      id="add-games-to-publisher"
      title="Add Games to Submissions"
      canSave={selectedGames.length === 0}
      actionText="SAVE"
      onSaveHandler={handleSave}
      Icon={Add}
      CustomControl={CustomControl}
      forceClose={false}
    >
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <FilterableGamesList games={availableGames} selectedGames={selectedGames} onToggleGame={handleToggleGame} />
      )}
    </FullScreenDialog>
  );
};
